import React, { useState, useEffect } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import { fmtGbp } from "@/lib/constants";

export function MaterialityPanel({ engagement, onSaved, locked }) {
    const [f, setF] = useState({
        benchmark: engagement.materiality_benchmark_gbp ?? "",
        pct: engagement.materiality_pct ?? 1,
        perf_pct: engagement.performance_pct ?? 70,
        triv_pct: engagement.trivial_pct ?? 5,
    });
    // Pull the firm-wide A02 defaults for any percentage this file hasn't set yet.
    useEffect(() => {
        api.get("/settings/materiality-defaults").then(({ data }) => {
            setF((cur) => ({
                ...cur,
                pct: engagement.materiality_pct ?? data.overall_pct,
                perf_pct: engagement.performance_pct ?? data.performance_pct,
                triv_pct: engagement.trivial_pct ?? data.trivial_pct,
            }));
        }).catch(() => {});
    }, [engagement.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const b = Number(f.benchmark) || 0;
    const overall = Math.round(b * (Number(f.pct) || 0)) / 100;
    const perf = Math.round(overall * (Number(f.perf_pct) || 0)) / 100;
    const triv = Math.round(overall * (Number(f.triv_pct) || 0)) / 100;
    const dirty = engagement.materiality_gbp !== overall || engagement.materiality_benchmark_gbp !== b;

    const save = async () => {
        try {
            await api.patch(`/engagements/${engagement.id}`, {
                materiality_benchmark_gbp: b, materiality_pct: Number(f.pct), materiality_gbp: overall,
                performance_pct: Number(f.perf_pct), performance_materiality_gbp: perf,
                trivial_pct: Number(f.triv_pct), trivial_gbp: triv,
            });
            toast.success("Materiality saved to the engagement");
            onSaved?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const inp = "border border-input rounded-sm px-2 py-1.5 text-sm font-num bg-white w-full disabled:bg-slate-50";

    return (
        <div className="bg-white border-2 border-brand/40 rounded-sm mb-5" data-testid="materiality-panel">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <Calculator className="w-4 h-4 text-brand" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold">Materiality calculation · flows to overview, lead sheets & completion summary</span>
            </div>
            <div className="p-4 grid grid-cols-4 gap-4 items-end">
                <L label="Benchmark: gross SC expenditure before reserve transfers (£)"><input data-testid="mat-benchmark" type="number" disabled={locked} value={f.benchmark} onChange={(e) => setF({ ...f, benchmark: e.target.value })} className={inp} /></L>
                <L label="Overall materiality %" hint="Range 0.5–2% · default 1%"><input data-testid="mat-pct" type="number" step="0.1" disabled={locked} value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} className={inp} /></L>
                <L label="Performance materiality %" hint="of overall · default 70%"><input data-testid="mat-perf-pct" type="number" disabled={locked} value={f.perf_pct} onChange={(e) => setF({ ...f, perf_pct: e.target.value })} className={inp} /></L>
                <L label="Clearly trivial %" hint="of overall · default 5%"><input data-testid="mat-triv-pct" type="number" disabled={locked} value={f.triv_pct} onChange={(e) => setF({ ...f, triv_pct: e.target.value })} className={inp} /></L>
            </div>
            <div className="px-4 pb-4 flex items-center gap-8">
                <Out label="Overall materiality" v={overall} testid="mat-overall" />
                <Out label="Performance materiality" v={perf} testid="mat-performance" />
                <Out label="Clearly trivial" v={triv} testid="mat-trivial" />
                {!locked && <button data-testid="mat-save-btn" onClick={save} disabled={!b} className="ml-auto bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-1.5 rounded-sm disabled:opacity-40">{dirty ? "Save materiality" : "Saved ✓"}</button>}
            </div>
        </div>
    );
}

function L({ label, hint, children }) {
    return <div><label className="text-[10px] uppercase tracking-wider text-muted-foreground font-display block mb-1 leading-tight">{label}</label>{children}{hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}</div>;
}
function Out({ label, v, testid }) {
    return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">{label}</div><div className="font-display font-bold text-xl font-num" data-testid={testid}>{v ? fmtGbp(v) : "—"}</div></div>;
}
